import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { AlertTriangle, CheckCircle2, Info, X } from "lucide-react";
import { cx } from "./ui.tsx";

type Tone = "success" | "danger" | "info";
interface Toast { id: number; tone: Tone; text: string }
const Ctx = createContext<(tone: Tone, text: string) => void>(() => {});

const ICON = { success: CheckCircle2, danger: AlertTriangle, info: Info };

/**
 * Polite live region (UX-16). Success/info fade after a few seconds; errors stay until dismissed.
 * Identical messages are not stacked. Success toasts are only raised after the server confirms (docs/06).
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const push = useCallback((tone: Tone, text: string) => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs.filter((x) => x.text !== text), { id, tone, text }].slice(-3));
    if (tone !== "danger") setTimeout(() => dismiss(id), 4500);
  }, [dismiss]);
  return (
    <Ctx.Provider value={push}>
      {children}
      <div aria-live="polite" role="status" className="pointer-events-none fixed inset-x-0 bottom-20 z-[60] flex flex-col items-center gap-2 px-4 md:bottom-6">
        {items.map((t) => {
          const Icon = ICON[t.tone];
          return (
            <div key={t.id}
              className={cx(
                "pointer-events-auto flex w-full max-w-md items-start gap-2.5 rounded-panel px-4 py-3 text-sm font-medium text-white shadow-e3 ring-1 ring-white/10 animate-rise-in",
                t.tone === "success" && "bg-brand",
                t.tone === "danger" && "bg-danger-ink",
                t.tone === "info" && "bg-info-ink",
              )}>
              <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span className="flex-1">{t.text}</span>
              <button type="button" aria-label="Fechar aviso" onClick={() => dismiss(t.id)} className="-m-1 rounded p-1 text-white/80 hover:bg-white/15 hover:text-white">
                <X className="size-4" aria-hidden />
              </button>
            </div>
          );
        })}
      </div>
    </Ctx.Provider>
  );
}

export const useToast = () => useContext(Ctx);
